import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { MatchScoreBadge } from '@/components/ui/MatchScoreBadge'
import { Pagination } from '@/components/ui/Pagination'
import { ProfileCard } from '@/components/ui/ProfileCard'
import { SuggestInput } from '@/components/ui/SuggestInput'
import { useToast } from '@/components/ui/toastContext'
import { CandidateDetailDrawer } from '@/features/recruiter/components/CandidateDetailDrawer'
import { ReminderTaskModal } from '@/features/recruiter/components/ReminderTaskModal'
import type { CandidateSearchFilters } from '@/features/recruiter/api/candidates.api'
import { useAiCandidateSearchStream } from '@/features/recruiter/hooks/useAiCandidateSearchStream'
import {
  useAiCandidateSearch,
  useCandidateFilterOptions,
  useCandidateSearchQuery,
} from '@/features/recruiter/hooks/useCandidates'
import type { ApiError } from '@/api/client'

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'experience_desc', label: 'Experience (High to Low)' },
  { value: 'recent_update', label: 'Recently Updated' },
]

function splitTags(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

export function CandidateSearchPage() {
  const toast = useToast()
  const [draft, setDraft] = useState({
    q: '',
    skills: '',
    current_location: '',
    experience_min_months: '',
    experience_max_months: '',
    notice_period_max_days: '',
    sort: 'relevance' as CandidateSearchFilters['sort'],
  })
  const [filters, setFilters] = useState<CandidateSearchFilters>({ sort: 'relevance', page: 1 })
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)
  const [reminderTarget, setReminderTarget] = useState<{ id: number; name: string } | null>(null)
  const [aiRequirement, setAiRequirement] = useState('')
  const aiStream = useAiCandidateSearchStream()

  const { data, isPending } = useCandidateSearchQuery(filters, !aiStream.isActive)
  const aiSearch = useAiCandidateSearch()
  const { data: filterOptions } = useCandidateFilterOptions()

  // Shared by both the plain filter search and the AI-ranked search — AI
  // ranking runs over exactly the same filter-narrowed pool, just with an
  // extra free-text requirement scored on top (see AiCandidateSearchController).
  const buildFilterPayload = () => ({
    q: draft.q || undefined,
    skills: splitTags(draft.skills),
    current_location: draft.current_location || undefined,
    experience_min_months: draft.experience_min_months
      ? Number(draft.experience_min_months)
      : undefined,
    experience_max_months: draft.experience_max_months
      ? Number(draft.experience_max_months)
      : undefined,
    notice_period_max_days: draft.notice_period_max_days
      ? Number(draft.notice_period_max_days)
      : undefined,
  })

  const hasAnyFilter = () => {
    const payload = buildFilterPayload()
    return Object.values(payload).some((v) => (Array.isArray(v) ? v.length > 0 : v != null))
  }

  const runSearch = () => {
    aiStream.clearRun()
    setFilters({ ...buildFilterPayload(), sort: draft.sort, page: 1 })
  }

  const runAiSearch = async () => {
    // The free-text box is optional — filters alone are folded into the AI
    // prompt too (see CandidateSearchRankingPromptBuilder), but at least
    // one of the two has to be given or there's nothing to rank against.
    if (!aiRequirement.trim() && !hasAnyFilter()) {
      toast.error('Set at least one filter or describe what you need before searching with AI.')
      return
    }

    try {
      const run = await aiSearch.mutateAsync({
        ...buildFilterPayload(),
        requirement: aiRequirement.trim() || undefined,
      })
      if (run.searchRunId === null || run.filteredCount === 0) {
        toast.info('No candidates matched your filters to rank — try broadening them.')
        return
      }
      aiStream.startRun(run.searchRunId, run.filteredCount)
    } catch (err) {
      toast.error((err as ApiError).message || 'AI search failed. Please try again.')
    }
  }

  return (
    <>
      <div className="my-4 page-header-breadcrumb">
        <p className="fw-semibold fs-20 mb-0">Candidate Search</p>
      </div>

      <div className="card custom-card mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Keyword (name, headline)"
                value={draft.q}
                onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <SuggestInput
                listId="skill-suggestions"
                placeholder="Skills (comma separated)"
                value={draft.skills}
                onChange={(value) => setDraft({ ...draft, skills: value })}
                suggestions={filterOptions?.skills ?? []}
              />
            </div>
            <div className="col-md-3">
              <SuggestInput
                listId="location-suggestions"
                placeholder="Location"
                value={draft.current_location}
                onChange={(value) => setDraft({ ...draft, current_location: value })}
                suggestions={filterOptions?.locations ?? []}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={draft.sort}
                onChange={(e) =>
                  setDraft({ ...draft, sort: e.target.value as CandidateSearchFilters['sort'] })
                }
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <input
                type="number"
                className="form-control"
                placeholder="Min experience (months)"
                value={draft.experience_min_months}
                onChange={(e) => setDraft({ ...draft, experience_min_months: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <input
                type="number"
                className="form-control"
                placeholder="Max experience (months)"
                value={draft.experience_max_months}
                onChange={(e) => setDraft({ ...draft, experience_max_months: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <input
                type="number"
                className="form-control"
                placeholder="Max notice period (days)"
                value={draft.notice_period_max_days}
                onChange={(e) => setDraft({ ...draft, notice_period_max_days: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <Button className="w-100" onClick={runSearch}>
                Search
              </Button>
            </div>
          </div>

          <hr />

          <div className="row g-2 align-items-end">
            <div className="col-md-9">
              <label htmlFor="ai-requirement" className="form-label fw-medium mb-1">
                Search with AI — describe what you need (optional if filters are set)
              </label>
              <textarea
                id="ai-requirement"
                className="form-control"
                rows={2}
                placeholder="e.g. Looking for a backend developer strong in Laravel and MySQL, comfortable working with a small team, available to join within 30 days."
                value={aiRequirement}
                onChange={(e) => setAiRequirement(e.target.value)}
              />
              <p className="fs-12 text-muted mb-0 mt-1">
                Applies your filters above first, then AI-ranks the matching candidates (up to
                the first 100 matches) against them and anything you describe here — filters
                alone work too, the free-text description isn't required.
              </p>
            </div>
            <div className="col-md-3">
              <Button className="w-100" variant="secondary" loading={aiSearch.isPending} onClick={runAiSearch}>
                Search with AI
              </Button>
            </div>
          </div>
        </div>
      </div>

      {aiStream.isActive ? (
        <>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              {!aiStream.isComplete && (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              )}
              <p className="text-muted mb-0">
                {aiStream.isComplete
                  ? `${aiStream.results.length} candidate${aiStream.results.length === 1 ? '' : 's'} ranked, highest match first.`
                  : `Scoring… ${aiStream.results.length} of ${aiStream.totalExpected} candidates ranked so far.`}
              </p>
            </div>
            <Button variant="ghost" onClick={aiStream.clearRun}>
              Clear AI ranking
            </Button>
          </div>
          {aiStream.isComplete && aiStream.results.length === 0 ? (
            <EmptyState
              heading="No candidates matched."
              message="Try broadening your filters, then search with AI again."
            />
          ) : (
            <div className="row g-3">
              {aiStream.results.map((result) => (
                <div className="col-12" key={result.studentProfileId}>
                  <ProfileCard
                    name={result.name}
                    program={result.program}
                    headline={result.headline}
                    photoUrl={result.photoUrl}
                    currentCity={result.currentCity}
                    currentState={result.currentState}
                    currentCompany={result.currentCompany}
                    currentDesignation={result.currentDesignation}
                    totalExperienceMonths={result.totalExperienceMonths}
                    isFresher={result.isFresher}
                    expectedCtc={result.expectedCtc}
                    noticePeriodDays={result.noticePeriodDays}
                    topSkills={result.topSkills}
                    topEducations={result.topEducations}
                    preferredLocations={result.preferredLocations}
                    profileCompletionPercent={result.profileCompletionPercent}
                    hasResume={result.hasResume}
                    updatedAt={result.updatedAt}
                    onViewDetail={() => setSelectedProfileId(result.studentProfileId)}
                    onScheduleReminder={() => setReminderTarget({ id: result.studentProfileId, name: result.name })}
                    extra={
                      <div>
                        <MatchScoreBadge score={result.aiScore} />
                        <p className="fs-12 text-muted mb-0 mt-1">{result.aiExplanation}</p>
                      </div>
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </>
      ) : isPending ? (
        <p className="text-muted">Searching…</p>
      ) : !data || data.results.length === 0 ? (
        <EmptyState heading="No candidates found." message="Try adjusting your filters." />
      ) : (
        <>
          <div className="row g-3">
            {data.results.map((result) => (
              <div className="col-12" key={result.studentProfileId}>
                <ProfileCard
                  name={result.name}
                  program={result.program}
                  headline={result.headline}
                  photoUrl={result.photoUrl}
                  currentCity={result.currentCity}
                  currentState={result.currentState}
                  currentCompany={result.currentCompany}
                  currentDesignation={result.currentDesignation}
                  totalExperienceMonths={result.totalExperienceMonths}
                  isFresher={result.isFresher}
                  expectedCtc={result.expectedCtc}
                  noticePeriodDays={result.noticePeriodDays}
                  topSkills={result.topSkills}
                  topEducations={result.topEducations}
                  preferredLocations={result.preferredLocations}
                  profileCompletionPercent={result.profileCompletionPercent}
                  hasResume={result.hasResume}
                  updatedAt={result.updatedAt}
                  onViewDetail={() => setSelectedProfileId(result.studentProfileId)}
                  onScheduleReminder={() => setReminderTarget({ id: result.studentProfileId, name: result.name })}
                />
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Pagination meta={data.meta} onPageChange={(page) => setFilters({ ...filters, page })} />
          </div>
        </>
      )}

      <CandidateDetailDrawer
        show={selectedProfileId != null}
        onClose={() => setSelectedProfileId(null)}
        studentProfileId={selectedProfileId ?? undefined}
      />

      <ReminderTaskModal
        show={reminderTarget != null}
        onClose={() => setReminderTarget(null)}
        studentProfileId={reminderTarget?.id ?? null}
        candidateName={reminderTarget?.name}
      />
    </>
  )
}
